import { Link } from '@tanstack/react-router';
import { BookOpen, ExternalLink, Copy, Check } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { PublicShell } from '@/components/layout/PublicShell';

function CodeBlock({ code, lang = 'bash' }: { code: string; lang?: string }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative group">
      <pre className="bg-muted rounded-md p-3 text-[11px] font-mono overflow-x-auto">
        <code>{code}</code>
      </pre>
      <Button
        size="sm"
        variant="outline"
        onClick={onCopy}
        className="absolute top-2 right-2 h-7 px-2 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      </Button>
      <span className="sr-only">{lang}</span>
    </div>
  );
}

export function ApiDocsPage() {
  const base = typeof window !== 'undefined' ? window.location.origin : 'https://bot.workercms.com';

  return (
    <PublicShell>
      <article className="container max-w-4xl py-12 space-y-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold flex items-center gap-2">
            <BookOpen className="w-7 h-7" /> CMS Hub API
          </h1>
          <p className="text-muted-foreground mt-2">
            REST API — programatik içerik üretimi ve WP dispatch. n8n, Zapier, Python scriptleri,
            custom backend'lerle entegre et.
          </p>
        </div>

        <section className="space-y-3">
          <h2 className="text-2xl font-semibold">Kimlik Doğrulama</h2>
          <p className="text-sm">
            <Link to="/account/api-keys" className="text-primary hover:underline">
              Hesap → API Anahtarları
            </Link>
            {' '}sayfasından key oluştur. Her istekte <code className="bg-muted px-1 py-0.5 rounded text-xs">Authorization: Bearer &lt;KEY&gt;</code> header'ını gönder.
          </p>
          <p className="text-xs text-muted-foreground">Rate limit: dakikada 60 istek / key.</p>
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-semibold">Temel URL</h2>
          <CodeBlock code={`${base}/api/v1`} />
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-semibold">Endpoint'ler</h2>

          <Endpoint
            method="GET"
            path="/me"
            desc="Organizasyon bilgisi + USD bakiye"
            example={`curl -H "Authorization: Bearer $CMS_HUB_KEY" \\
  ${base}/api/v1/me`}
            response={`{
  "ok": true,
  "data": {
    "organization": {
      "id": "org_xxx",
      "name": "...",
      "credit_balance": 42000,
      "current_package_id": "pro"
    }
  }
}`}
          />

          <Endpoint
            method="GET"
            path="/models"
            desc="Desteklenen AI modelleri (id, µUSD/kelime, family)"
            example={`curl -H "Authorization: Bearer $CMS_HUB_KEY" \\
  ${base}/api/v1/models`}
          />

          <Endpoint
            method="GET"
            path="/sites"
            desc="Organizasyondaki siteler (id, URL, platform, SEO plugin)"
            example={`curl -H "Authorization: Bearer $CMS_HUB_KEY" \\
  ${base}/api/v1/sites`}
          />

          <Endpoint
            method="POST"
            path="/content/generate"
            desc="One-shot: taslak oluştur → AI üret → opsiyonel dispatch"
            example={`curl -X POST \\
  -H "Authorization: Bearer $CMS_HUB_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "focus_keyword": "ev taşıma ipuçları",
    "language": "Türkçe",
    "tone": "Profesyonel",
    "audience": "Genel okuyucu",
    "article_type": "general",
    "section_length": "medium",
    "ai_model": "gpt-4o-mini",
    "include_faq": true,
    "web_grounded": false,
    "site_id": "site_abc",
    "auto_dispatch": true,
    "dispatch_status": "publish"
  }' \\
  ${base}/api/v1/content/generate`}
            response={`{
  "ok": true,
  "data": {
    "content": { ... },
    "credits_consumed": 1200,
    "word_count": 1100,
    "dispatched": true,
    "remote_post_id": 4521,
    "remote_post_url": "https://siten.com/..."
  }
}`}
          />

          <Endpoint
            method="GET"
            path="/content"
            desc="Son 100 içerik (taslaklar + yayınlananlar)"
          />

          <Endpoint
            method="GET"
            path="/content/:id"
            desc="Tek içerik detayı"
          />

          <Endpoint
            method="POST"
            path="/content/:id/dispatch"
            desc="Var olan içeriği WP'ye gönder"
            example={`curl -X POST \\
  -H "Authorization: Bearer $CMS_HUB_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"site_id": "site_abc", "status": "publish"}' \\
  ${base}/api/v1/content/cnt_xxx/dispatch`}
          />
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-semibold">Hata Kodları</h2>
          <table className="w-full text-sm">
            <thead className="border-b">
              <tr>
                <th className="text-left py-2">Code</th>
                <th className="text-left py-2">HTTP</th>
                <th className="text-left py-2">Anlam</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              <tr><td className="py-2"><code>no_api_key</code></td><td>401</td><td>Bearer header yok</td></tr>
              <tr><td className="py-2"><code>invalid_key</code></td><td>401</td><td>Geçersiz/devre dışı key</td></tr>
              <tr><td className="py-2"><code>rate_limit</code></td><td>429</td><td>Dakikalık 60 istek aşıldı</td></tr>
              <tr><td className="py-2"><code>insufficient_credits</code></td><td>402</td><td>Kredi yetmiyor</td></tr>
              <tr><td className="py-2"><code>bad_model</code></td><td>400</td><td>Model id geçersiz</td></tr>
              <tr><td className="py-2"><code>invalid_site</code></td><td>400</td><td>Site yok / bu org'a ait değil</td></tr>
              <tr><td className="py-2"><code>generation_failed</code></td><td>502</td><td>AI hatası</td></tr>
              <tr><td className="py-2"><code>dispatch_failed</code></td><td>502</td><td>WP'ye post gönderilemedi</td></tr>
            </tbody>
          </table>
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-semibold">Örnek: Python ile</h2>
          <CodeBlock
            code={`import os, requests

KEY = os.environ['CMS_HUB_KEY']
BASE = '${base}/api/v1'

def generate(keyword, site_id=None):
    r = requests.post(
        f'{BASE}/content/generate',
        headers={'Authorization': f'Bearer {KEY}'},
        json={
            'focus_keyword': keyword,
            'site_id': site_id,
            'auto_dispatch': site_id is not None,
            'ai_model': 'gpt-4o-mini',
            'section_length': 'medium',
        },
    )
    r.raise_for_status()
    return r.json()['data']

# Toplu üretim
for kw in ['ev taşıma', 'kredi kartı', 'köpek bakımı']:
    result = generate(kw, site_id='site_abc')
    print(kw, "→ $", result['credits_consumed'] / 1_000_000, result.get('remote_post_url'))`}
            lang="python"
          />
        </section>

        <section className="space-y-3">
          <h2 className="text-2xl font-semibold">n8n ile</h2>
          <p className="text-sm">
            n8n'de <strong>HTTP Request</strong> node'u:
            <br />
            Method: POST, URL: <code className="bg-muted px-1 py-0.5 rounded">{base}/api/v1/content/generate</code>
            <br />
            Authentication: <strong>Header Auth</strong> → Name: <code className="bg-muted px-1 py-0.5 rounded">Authorization</code>, Value: <code className="bg-muted px-1 py-0.5 rounded">Bearer &lt;KEY&gt;</code>
          </p>
        </section>

        <section className="pt-8 border-t text-center text-sm text-muted-foreground">
          <p>
            Sorular için <a href="mailto:destek@workercms.com" className="text-primary hover:underline">destek@workercms.com</a>
          </p>
        </section>
      </article>
    </PublicShell>
  );
}

function Endpoint({
  method,
  path,
  desc,
  example,
  response,
}: {
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  path: string;
  desc: string;
  example?: string;
  response?: string;
}) {
  const methodColor = {
    GET: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
    POST: 'bg-primary/10 text-primary',
    PATCH: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
    DELETE: 'bg-destructive/10 text-destructive',
  }[method];

  return (
    <div className="rounded-md border p-4 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-mono font-semibold ${methodColor}`}>
          {method}
        </span>
        <code className="font-mono text-sm">{path}</code>
      </div>
      <p className="text-sm text-muted-foreground">{desc}</p>
      {example && <CodeBlock code={example} />}
      {response && (
        <>
          <div className="text-xs text-muted-foreground font-semibold">Yanıt:</div>
          <CodeBlock code={response} lang="json" />
        </>
      )}
    </div>
  );
}

// Silence unused import
void ExternalLink;
